import { Container, Box, Typography, makeStyles } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

const EFFECTIVE_DATE = "July 21, 2026";

const useStyles = makeStyles((theme) => ({
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  heroSection: {
    position: "relative",
    overflow: "hidden",
    background:
      "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
    textAlign: "center",
    padding: "52px 20px 44px",
  },
  heroLogo: {
    width: 76,
    height: 76,
    borderRadius: 20,
    display: "block",
    margin: "0 auto 16px",
    boxShadow: "0 14px 36px rgba(0,0,0,0.5)",
    animation: "$heroFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) both",
  },
  heroTitle: {
    fontSize: "clamp(1.4rem, 3.2vw, 1.9rem)",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 6,
    animation: "$heroFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both",
  },
  heroSub: {
    fontSize: "0.84rem",
    color: "rgba(255,255,255,0.7)",
    animation: "$heroFadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both",
  },
  pageWrapper: {
    background: "#f5f3f6",
    padding: "36px 0 56px",
  },
  policyCard: {
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid rgba(76,0,76,0.07)",
    boxShadow: "0 10px 40px rgba(76,0,76,0.07)",
    padding: "36px 40px",
    maxWidth: 780,
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: { padding: "26px 22px" },
  },
  effective: {
    fontSize: "0.74rem",
    fontWeight: 600,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.6px",
    marginBottom: 20,
  },
  h2: {
    fontSize: "1.02rem",
    fontWeight: 700,
    color: "#3a003a",
    margin: "26px 0 8px",
  },
  p: {
    fontSize: "0.86rem",
    color: "#444444",
    lineHeight: 1.75,
    marginBottom: 10,
    overflowWrap: "anywhere",
  },
  ul: {
    margin: "0 0 10px",
    paddingLeft: 22,
    "& li": {
      fontSize: "0.86rem",
      color: "#444444",
      lineHeight: 1.75,
      marginBottom: 5,
    },
  },
  a: {
    color: "#6b1b6b",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline", color: "#4c004c" },
  },
  backRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: 30,
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#4c004c",
    background: "#ffffff",
    border: "1px solid rgba(76,0,76,0.2)",
    borderRadius: 11,
    padding: "10px 22px",
    textDecoration: "none",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#4c004c",
      textDecoration: "none",
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 26px rgba(76,0,76,0.14)",
    },
    "& svg": { fontSize: 16 },
  },
}));

export const RogueLanesPrivacy = () => {
  const classes = useStyles();
  return (
    <div>
      <Helmet>
        <title>Rogue Lanes Privacy Policy | Arawinz Soft Solutions</title>
        <meta
          name="description"
          content="Privacy policy for Rogue Lanes, the endless highway racer by Arawinz Soft Solutions, covering the Android and iOS apps."
        />
      </Helmet>

      <Box className={classes.heroSection}>
        <img
          src="/static/products/roguelanes.png"
          alt="Rogue Lanes"
          className={classes.heroLogo}
        />
        <Typography className={classes.heroTitle}>
          Rogue Lanes Privacy Policy
        </Typography>
        <Typography className={classes.heroSub}>
          Arawinz Soft Solutions Pvt. Ltd.
        </Typography>
      </Box>

      <Box className={classes.pageWrapper}>
        <Container maxWidth="md">
          <Box className={classes.policyCard}>
            <Typography className={classes.effective}>
              Effective date: {EFFECTIVE_DATE}
            </Typography>

            <Typography className={classes.p}>
              This privacy policy describes how Rogue Lanes ("the game"),
              published by Arawinz Soft Solutions Pvt. Ltd. ("we", "us"),
              handles information on Android and iOS.
            </Typography>

            <Typography className={classes.h2}>The short version</Typography>
            <ul className={classes.ul}>
              <li>The game does not ask for an account and does not collect your name, email, phone number, contacts, or location.</li>
              <li>Gameplay works fully offline. All progress is stored on your device only.</li>
              <li>We do not use any analytics or crash-reporting services.</li>
              <li>Ads are served by Google AdMob, which processes some device data under Google's own privacy policy.</li>
              <li>We do not sell your data. We never see it in the first place.</li>
            </ul>

            <Typography className={classes.h2}>
              Data stored on your device
            </Typography>
            <Typography className={classes.p}>
              The game saves your progress locally on your device: settings
              (sound, music, vibration, control style), coins and unlocked
              vehicles, upgrade levels, missions, XP and level, best scores and
              recent run history, and a flag that you have seen the tutorial.
              This data never leaves your device. You can erase it at any time
              with "Reset progress" in the game's settings, or by uninstalling
              the game.
            </Typography>

            <Typography className={classes.h2}>Advertising (Google AdMob)</Typography>
            <Typography className={classes.p}>
              The game shows ads through Google AdMob: optional rewarded ads
              (to revive after a crash or double a run's coins, both entirely
              your choice) and an occasional interstitial between runs. To
              serve and measure ads, Google may process device information such
              as your device's advertising identifier, IP address, and ad
              interaction data. This processing is governed by{" "}
              <a
                className={classes.a}
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google's Privacy Policy
              </a>
              . Where required by law, the game shows a consent form before ads
              are personalized, and you can change your choice there. You can
              also reset or delete your advertising identifier in your device
              settings (Android: Settings, Google, Ads. iOS: Settings, Privacy
              and Security, Tracking). On iOS, the game will only request
              tracking permission through Apple's App Tracking Transparency
              prompt, and you can decline it.
            </Typography>

            <Typography className={classes.h2}>Permissions</Typography>
            <Typography className={classes.p}>
              On Android the game requests a single permission: internet
              access, used only for ads. The game
              does not request location, camera, microphone, storage, or
              contact permissions on any platform.
            </Typography>

            <Typography className={classes.h2}>Children</Typography>
            <Typography className={classes.p}>
              The game is designed for a general audience and does not
              knowingly collect personal information from anyone, including
              children. Ads are served through Google AdMob in line with
              Google's advertising policies.
            </Typography>

            <Typography className={classes.h2}>
              Data sharing and selling
            </Typography>
            <Typography className={classes.p}>
              We do not sell, rent, or share personal information. The only
              third party involved is Google AdMob (advertising), as described
              above.
            </Typography>

            <Typography className={classes.h2}>Deleting your data</Typography>
            <Typography className={classes.p}>
              All game progress lives on your device: use "Reset progress" in
              the game's settings or uninstall the game to remove it.
              Advertising data held by Google can be managed through your
              Google account settings.
            </Typography>

            <Typography className={classes.h2}>Changes to this policy</Typography>
            <Typography className={classes.p}>
              If we change how the game handles data, we will update this page
              and its effective date before the change takes effect.
            </Typography>

            <Typography className={classes.h2}>Contact</Typography>
            <Typography className={classes.p}>
              Questions about this policy or your data? Email us at{" "}
              <a className={classes.a} href="mailto:contact@arawinz.com">
                contact@arawinz.com
              </a>{" "}
              (Arawinz Soft Solutions Pvt. Ltd.).
            </Typography>
          </Box>

          <Box className={classes.backRow}>
            <Link to="/product/rogue-lanes" className={classes.backBtn}>
              <ArrowBackIcon /> Back to Rogue Lanes
            </Link>
          </Box>
        </Container>
      </Box>
    </div>
  );
};
